/**
 * Safely evaluates a mathematical expression.
 * Returns the evaluated result or null if invalid.
 * Export this so parent components can use it when needed.
 */

export function evaluateExpression(expr: string): number | null {
  try {
    // Remove whitespace
    const cleaned = expr.trim();

    // If it's just a number, return it
    const directNumber = parseFloat(cleaned);
    if (!isNaN(directNumber) && cleaned === String(directNumber)) {
      return directNumber;
    }

    // Only allow numbers, operators, parentheses, and decimal points
    if (!/^[\d+\-*/(). ]+$/.test(cleaned)) {
      return null;
    }

    // Prevent dangerous patterns
    if (cleaned.includes('..') || /[+\-*/]{2,}/.test(cleaned.replace(/\*\*/g, ''))) {
      return null;
    }

    // Evaluate using Function constructor (safer than eval)

    const result = new Function(`'use strict'; return (${cleaned})`)();

    // Verify result is a valid number
    if (typeof result === 'number' && !isNaN(result) && isFinite(result)) {
      return result;
    }

    return null;
  } catch {
    return null;
  }
}

export function validateCSVFormat(csvString: string): string | null {
  let s = csvString.trim();
  if (!s) return null;

  if (/[\r\n]/.test(s)) return 'CSV must be a single line';

  // Convert Excel paste separators (tabs / multiple spaces) into commas
  // - tabs -> commas
  // - runs of 2+ spaces -> comma (keeps negatives/decimals safe)
  s = s.replace(/\t+/g, ',').replace(/ {2,}/g, ',');

  // Also allow single spaces ONLY if there are no commas/tabs, treat as separator
  if (!s.includes(',') && s.includes(' ')) {
    s = s.replace(/ +/g, ',');
  }

  // allowed chars after normalization - now includes expression operators
  if (!/^[0-9,\s.+\-*/()]+$/.test(s)) return 'Only numbers, expressions, and separators are allowed';

  // comma sanity
  if (s.startsWith(',') || s.endsWith(',')) return 'List cannot start or end with a separator';
  if (s.includes(',,')) return 'List cannot contain empty values (double separators)';

  const tokens = s.split(',').map(t => t.trim());
  if (tokens.some(t => t.length === 0)) return 'List cannot contain empty values';

  // Validate each token can be evaluated to a number
  for (const t of tokens) {
    const evaluated = evaluateExpression(t);
    if (evaluated === null) {
      return `Invalid value "${t}". Each value must be a valid number or expression.`;
    }
  }

  return null;
}

/**
 * Simple CSV line parser that handles quoted values.
 * Returns array of values or null if invalid.
 */
export function parseCSVLine(line: string): string[] | null {
  try {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    let i = 0;

    while (i < line.length) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Escaped quote
          current += '"';
          i += 2;
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
          i++;
        }
      } else if (char === ',' && !inQuotes) {
        // Field separator
        values.push(current.trim());
        current = '';
        i++;
      } else {
        current += char;
        i++;
      }
    }

    // Add the last field
    values.push(current.trim());

    // Check for unclosed quotes
    if (inQuotes) {
      return null;
    }

    return values;
  } catch {
    return null;
  }
}

export function validateRebarFormat(rebarString: string): string | null {
  const err = validateCSVFormat(rebarString);
  if (err) return err;

  const tokens = rebarString.split(',').map(t => t.trim());
  for (const t of tokens) {
    const evaluated = evaluateExpression(t);
    if (evaluated === null) {
      return `Invalid value "${t}". Each value must be a valid number or expression.`;
    }
  }

  return null;
}
